import { useEffect, useState, useCallback } from 'react';
import { useProgram } from './useProgram';
import { PublicKey } from '@solana/web3.js';
import { Buffer } from 'buffer'
import { MONOPOLY_MINT, ROLL_MINT } from '../constants';
import { getAccount, getAssociatedTokenAddress } from '@solana/spl-token';

// TODO: properly inject as a polyfill instead of hacking
(window as typeof window & { Buffer: typeof Buffer }).Buffer = Buffer

export interface UserStakeData {
  staked_amount: bigint;
  reward_debt: bigint;
}

export interface Balances {
  monopolyBalance: bigint;
  rollBalance: bigint;
}

export function useUserStake(userPubkey: PublicKey | null, poolState: any) {
  const { connection, programId } = useProgram();
  const [userStakeData, setUserStakeData] = useState<UserStakeData | null>(null);
  const [userBalances, setUserBalances] = useState<Balances>({ monopolyBalance: null, rollBalance: null });
  const [loading, setLoading] = useState(true);

  const deriveUserStakePDA = useCallback(async () => {
    if (!userPubkey || !poolState) return null;
    // The user stake PDA: seeds = [b"user_stake", pool.key(), user.key()]
    // We must know the pool pda here. Let's assume we can derive from poolState:
    // If poolState doesn't store pool PDA directly, re-derive it:
    const [poolPDA] = PublicKey.findProgramAddressSync(
      [Buffer.from('pool3'), (new PublicKey(poolState.monopoly_mint)).toBuffer(), (new PublicKey(poolState.roll_mint)).toBuffer()],
      programId
    );

    const [userPda] = await PublicKey.findProgramAddress(
      [Buffer.from('user_stake'), poolPDA.toBuffer(), userPubkey.toBuffer()],
      programId
    );
    return userPda;
  }, [userPubkey, poolState, programId]);

  const fetchUserStake = useCallback(async () => {
    if (!userPubkey || !poolState) {
      setUserStakeData(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const userStakePda = await deriveUserStakePDA();
      if (!userStakePda) {
        setUserStakeData(null);
        setLoading(false);
        return;
      }

      const accountInfo = await connection.getAccountInfo(userStakePda);
      if (!accountInfo) {
        // user stake account not created
        setUserStakeData(null);
      } else {
        const data = accountInfo.data;
        const DISCRIMINATOR_OFFSET = 0;

        const buffer = Buffer.from(data);
        const OWNER_OFFSET = DISCRIMINATOR_OFFSET + 8;
        const STAKED_AMOUNT_OFFSET = OWNER_OFFSET + 32;      // 40
        const REWARD_DEBT_OFFSET = STAKED_AMOUNT_OFFSET + 8; // 48
        //const BUMP_OFFSET = REWARD_DEBT_OFFSET + 16;         // 64

        const owner = new PublicKey(buffer.slice(OWNER_OFFSET, OWNER_OFFSET + 32));
        const staked_amount = buffer.readBigUInt64LE(STAKED_AMOUNT_OFFSET);
        const reward_debt_low = buffer.readBigUInt64LE(REWARD_DEBT_OFFSET);
        const reward_debt_high = buffer.readBigUInt64LE(REWARD_DEBT_OFFSET + 8);
        const reward_debt = reward_debt_low + (reward_debt_high << 64n);
        //const bump = buffer.readUInt8(BUMP_OFFSET);

        setUserStakeData({
          staked_amount,
          reward_debt,
        });
      }

      const userMonopolyAccount = await getAssociatedTokenAddress(
        new PublicKey(MONOPOLY_MINT),
        userPubkey
      );
      const monopolyAccountInfo = await getAccount(connection, userMonopolyAccount)

      let rollBalance = null
      try {
        const userRollAccount = await getAssociatedTokenAddress(
          new PublicKey(ROLL_MINT),
          userPubkey
        );
        const rollAccountInfo = await getAccount(connection, userRollAccount)

        rollBalance = rollAccountInfo.amount
      } catch (e) {
      }

      setUserBalances({
        monopolyBalance: monopolyAccountInfo.amount,
        rollBalance,
      });

    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }, [userPubkey, poolState, connection, deriveUserStakePDA]);

  useEffect(() => {
    fetchUserStake();
  }, [fetchUserStake]);

  return { userStakeData, userBalances, loading, refresh: fetchUserStake };
}

